/**
 * The chrome class map `<schmancy-input>`'s `<input>` renders — the single source
 * of truth for the field's outer treatment (radius, border, background, ring,
 * focus, placeholder, disabled). Any host that composes a third-party control in
 * place of a real `<input>` (schmancy-revolute's card-field wrapper mounts a
 * Revolut SDK iframe where an `<input>` would otherwise sit) draws from this SAME
 * function, so a chrome change here cascades to both automatically instead of
 * drifting through a hand-copied class list.
 *
 * `focusWithin` swaps every `focus:` variant for `focus-within:` — a wrapper div
 * around an iframe never receives focus itself; the iframe's own document does,
 * and `:focus-within` normally observes that. Opaque provider frames may also
 * need a host focus bridge, as the payment wallet sections do. Both branches
 * spell every class out verbatim (never built via template-string
 * interpolation) because Tailwind's JIT scanner extracts class candidates from
 * static source text only.
 *
 * `variant` `filled` trades the ground-matching fill and soft outline for a
 * translucent `surface-on` tint — a recess on any ground in both schemes — and
 * keeps a transparent 1px border so its box measures exactly as `outlined`'s.
 */
export function fieldChromeClasses(
	error: boolean,
	focusWithin = false,
	variant: 'outlined' | 'filled' | 'bare' = 'outlined',
): Record<string, boolean> {
	const focusVariantClasses = focusWithin
		? {
				'outline-secondary-default focus-within:outline-1 focus-within:border-secondary-default': true,
				'ring-0 focus-within:ring-1 focus-within:ring-inset': true,
				'focus-within:ring-secondary-default': !error,
				'focus-within:ring-error-default': error,
			}
		: {
				'outline-secondary-default focus:outline-1 focus:border-secondary-default': true,
				'ring-0 focus:ring-1 focus:ring-inset': true,
				'focus:ring-secondary-default': !error,
				'focus:ring-error-default': error,
			}

	return {
		// rounded-xl (14px) — the field register. The ladder's `lg` rung was proven
		// against a 40px-tall control, but a real field measures 48px, where 10px is
		// 21% of the height and reads flat/Material. 14px is 29% at 48px — the same
		// proportion the ladder's own proof defended as the iOS register for a 48px
		// control. input.scss's unlayered `input {}`/date-type resets track this rung
		// (they exist for a real defensive reason: native appearance reset and
		// date/time UA chrome reasserting their own corner after appearance:none).
		'block w-full min-w-0 border text-surface-on': true,
		'rounded-xl': variant !== 'bare',
		'rounded-xs bg-transparent border-transparent enabled:hover:bg-surface-container focus:bg-surface-container': variant === 'bare' && !error,
		'rounded-xs bg-transparent enabled:hover:bg-surface-container focus:bg-surface-container': variant === 'bare' && error,
		'bg-surface-containerLowest': variant === 'outlined',
		'border-control-border': variant === 'outlined' && !error,
		// `enabled:` keeps a disabled well from answering a hover it cannot act on.
		'bg-surface-on/5 enabled:hover:bg-surface-on/10': variant === 'filled',
		'border-transparent': variant === 'filled' && !error,
		'border-error-default': error,
		'placeholder:text-muted placeholder:italic': variant !== 'bare',
		'placeholder:text-surface-onVariant': variant === 'bare',
		'disabled:opacity-40 disabled:cursor-not-allowed': true,
		...focusVariantClasses,
	}
}

/**
 * Same class set as `fieldChromeClasses`, flattened to a class-name string — for
 * imperative (non-Lit-template) consumers that write a ref's `className`
 * directly rather than binding through `classMap`. schmancy-revolute carries its
 * card-field wrapper's error state this way, matching the rest of that file's
 * ref-driven, non-declarative-render architecture.
 */
export function fieldChromeClassNames(
	error: boolean,
	focusWithin = false,
	variant: 'outlined' | 'filled' = 'outlined',
): string {
	return Object.entries(fieldChromeClasses(error, focusWithin, variant))
		.filter(([, active]) => active)
		.map(([className]) => className)
		.join(' ')
}
